import { getResourceString, LIST_SCHEMA_BRANCHES } from '@takeshape/actions';
import { setProjectEndpoint } from 'app/graphql-client';
import { getPermission } from 'app/permissions/selectors';
import { setCurrentProject } from 'app/project/actions';
import some from 'lodash/some';
import type { SagaIterator } from 'redux-saga';
import { all, put, select, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { resetBranchState, setCurrentBranch } from '../actions.js';
import { DEFAULT_BRANCH } from '../constants.js';
import { getCurrentBranchName } from '../selectors.js';
import { getBranchListRoutine } from './get-branch-list.js';

export function* initBranchSaga(action: ActionType<typeof setCurrentProject>): SagaIterator {
  // `setCurrentProject` can be called with an empty string, so we need to check if we're actually in a project
  const { projectId, branch } = action.payload;
  yield put(resetBranchState());
  if (projectId) {
    yield put(setCurrentBranch({ branch, snackbarMessage: false }));
    setProjectEndpoint(projectId, branch);
    const hasPermission = yield select(getPermission, {
      action: LIST_SCHEMA_BRANCHES,
      resource: getResourceString('project', { projectId })
    });
    if (hasPermission) {
      yield put(getBranchListRoutine.requested());
    }
  }
}

export function* handleBranchListFulfilled({
  payload
}: ActionType<typeof getBranchListRoutine.fulfilled>): SagaIterator {
  const currentBranchName: string = yield select(getCurrentBranchName);
  if (
    currentBranchName !== DEFAULT_BRANCH &&
    !some(payload.branches, (branch) => branch.branchName === currentBranchName)
  ) {
    yield put(
      setCurrentBranch({
        branch: 'production',
        message: `Branch "${currentBranchName}" not found. Switching to production.`,
        snackbarMessage: !currentBranchName
      })
    );
  }
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(setCurrentProject, initBranchSaga),
    takeLatest(getBranchListRoutine.fulfilled, handleBranchListFulfilled)
  ]);
}
